import { removeElement } from '@/modules/globalModule/components/helperMethods';
import { useNodeCanvasStore } from '../../store';
import { CanvasItem, Position } from './canvasObjects';
import { V4UUID } from '@/firebase/database';
import { v4 } from 'uuid';
import { useGlobalStore } from '@/modules/globalModule/store';

export class Node implements CanvasItem {
  position: Position;
  radius: number;
  language: string = 'py';
  color: string;
  name: string;
  id: V4UUID;
  publishers: V4UUID[];
  subscribers: V4UUID[];
  parameters: Array<NodeParameter>;
  packageId?: V4UUID;
  customText: CustomNodeText = {
    importText: '',
    constructorText: '',
    mainText: '',
    methodsText: '',
    callbacks: []
  };
  cachedCustomText: CustomNodeText = {
    importText: '',
    constructorText: '',
    mainText: '',
    methodsText: '',
    callbacks: [],
    privateMethodsDefText: '',
    publicMethodsDefText: ''
  } as CustomCppNodeText; // custom text stored if node switches languages
  /**
   * @param {*} id
   * @param {*} x
   * @param {*} y
   * @param {*} name
   * @param {*} radius default - 50
   * @param {*} color - "skyblue"
   * @param {*} language - "py"
   * @param {*} publishers - []
   * @param {*} subscribers - []
   * @param {*} parameters - {name: str, type: str, value: str, id: v4uuid}[]
   */
  constructor(
    id: V4UUID = v4(),
    x: number = 0,
    y: number = 0,
    name: string = '',
    radius: number = 50,
    color: string = 'skyblue',
    language: string = 'py',
    publishers: V4UUID[] = [],
    subscribers: V4UUID[] = [],
    parameters: Array<NodeParameter> = [],
    packageId: V4UUID | undefined = undefined,
    customText: CustomNodeText | undefined = undefined,
    cachedCustomText: CustomNodeText | undefined = undefined
  ) {
    this.id = id;
    this.position = new Position(x, y);
    this.name = name;
    this.radius = radius;
    this.color = color;
    this.language = language; // either "py" or "cpp"
    this.publishers = publishers;
    this.subscribers = subscribers;
    this.parameters = parameters;
    this.packageId = packageId;
    if (customText) this.customText = customText;
    if (cachedCustomText) this.cachedCustomText = cachedCustomText;
    console.log(
      `building node: custom text: ${customText}, node customtext: ${this.customText}, ${cachedCustomText}, cached: ${this.cachedCustomText}`
    );
    if (language === 'cpp' && !customText) this.cacheCustomText();
  }
  addPublisher(topicId: V4UUID) {
    if (this.publishers.indexOf(topicId) === -1) {
      this.publishers.push(topicId);
    }
  }
  addSubscriber(topicId: V4UUID) {
    if (this.subscribers.indexOf(topicId) === -1) {
      this.subscribers.push(topicId);
    }
  }
  removePublisher(topicId: V4UUID) {
    if (this.publishers.indexOf(topicId) !== -1) {
      removeElement(this.publishers, topicId);
    }
  }
  removeSubscriber(topicId: V4UUID) {
    if (this.subscribers.indexOf(topicId) !== -1) {
      removeElement(this.subscribers, topicId);
    }
  }
  addParameter(param: NodeParameter) {
    if (this.parameters.find((ea) => ea.id === param.id) !== undefined) {
      return false;
    } else {
      this.parameters.push(param);
      return true;
    }
  }
  removeParameter(id: string) {
    this.parameters = this.parameters.filter((ea) => ea.id !== id);
  }
  setLanguage(newLanguage: string) {
    if (this.language !== newLanguage) this.cacheCustomText();
    this.language = newLanguage;
  }

  getLanguage(): string {
    return this.language;
  }

  /**
   * Used when switching between python and c++,
   * swaps the cached custom text with the currently
   * used custom text
   */
  cacheCustomText(): void {
    const customTextTemp = this.customText;
    this.customText = this.cachedCustomText;
    this.cachedCustomText = customTextTemp;
  }

  // TODO: move this to draw canvas
  drawArcs(stage: any, drawLineFunc: any): void {
    const ncStore = useNodeCanvasStore();
    const globalStore = useGlobalStore();
    if (!ncStore.packageId) return;
    let linesDrawn: Array<Position> = [];
    for (let topicId of [...this.subscribers, ...this.publishers]) {
      if (!ncStore.packageId) return;
      const topic = globalStore.getTopic(topicId);
      if (
        topic &&
        globalStore.checkElemInPackage(ncStore.packageId, topicId) &&
        !linesDrawn.includes(topic.position)
      ) {
        drawLineFunc(stage, this, topic);
        linesDrawn.push(topic.position);
      }
    }
  }
  fromJSON(args: any) {
    return Node.fromJSON(args);
  }
  /**
   * @param {*} jsonNode - The node as an object converted from a json string
   */
  static fromJSON(jsonNode: any) {
    let node = new Node(
      jsonNode.id,
      jsonNode.position ? jsonNode.position.x : jsonNode.x,
      jsonNode.position ? jsonNode.position.y : jsonNode.y,
      jsonNode.name,
      jsonNode.radius,
      jsonNode.color,
      jsonNode.language,
      jsonNode.publishers,
      jsonNode.subscribers,
      jsonNode.parameters,
      jsonNode.packageId,
      jsonNode.customText,
      jsonNode.cachedCustomText
    );
    if (!node.customText.callbacks) node.customText.callbacks = [];
    return node;
  }

  toJSON() {
    const nodeCopy = {
      position: this.position,
      radius: this.radius,
      language: this.language,
      color: this.color,
      name: this.name,
      id: this.id,
      publishers: this.publishers,
      subscribers: this.subscribers,
      parameters: this.parameters,
      packageId: this.packageId,
      customText: this.customText,
      cachedCustomText: this.cachedCustomText
    };
    return nodeCopy;
  }

  setPosition = (x: number, y: number) => (this.position = { x: x, y: y });
  getPosition: () => Position = () => this.position;
}

export class NodeParameter {
  name: string;
  type: string;
  value: string;
  id: string;
  constructor(id: string, name: string, type: string, value: string) {
    this.name = name;
    this.id = id;
    this.type = type;
    this.value = value;
  }
}

export interface CustomNodeText {
  importText: string;
  constructorText: string; // constructor text
  mainText: string;
  methodsText: string; // method implementation text
  callbacks: Array<{ name: string; text: string }>;
}

export interface CustomCppNodeText extends CustomNodeText {
  publicMethodsDefText: string;
  privateMethodsDefText: string;
}
